import * as React from "react";

import { cn } from "../../lib/utils";

/** Compact label/value pair for detail-pane sidebars. Supersedes `info-row.tsx`. */

export interface PropertyRowProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onClick" | "children"
> {
  /** Property name, e.g. "Assignee". */
  label: string;
  /** Optional leading glyph next to the label. */
  icon?: React.ReactNode;
  /** The value — plain text, a chip, or an inline control. */
  children: React.ReactNode;
  /** Makes the whole row a menu trigger (keyboard operable). */
  onClick?: () => void;
  /** Renders the hover affordance without wiring a click handler. */
  editable?: boolean;
  /** Width of the label column. Default `w-28` (112px). */
  labelWidth?: string;
  /**
   * Rendered immediately after the label text, inside the label column.
   *
   * For a glyph that belongs to the label — an info hint, a lock. It sits
   * inside the column so it reads as part of the name; anything placed after
   * the *value* instead lands at a different x on every row, because the value
   * it follows is a different width each time.
   */
  labelSuffix?: React.ReactNode;
  /**
   * `"text"` (default) truncates the value, which is right for plain text.
   *
   * `"control"` drops the truncation so a focus ring can escape the value
   * column. `truncate` implies `overflow: hidden`, which clips the 2px ring of
   * an inline `Select` or `Picker` sitting in the row down to nothing — the
   * control is then focusable with no visible focus indicator at all. Use it
   * for every row whose value *is* an interactive control; the control does
   * its own truncating.
   */
  valueVariant?: "text" | "control";
  /**
   * Leading glyph for the VALUE, rendered in a fixed-width slot so text starts
   * at the same x on every row — which is what makes a stack of rows read as a
   * table instead of a ragged list.
   *
   * Tri-state on purpose:
   * - `undefined` (default) — no slot at all. Existing call sites are unchanged.
   * - a node — slot reserved, glyph centred in it.
   * - `null` — slot reserved and left EMPTY. This is the one that matters: a row
   *   whose value is plain text has to reserve the same gutter as its
   *   neighbours, or it hangs 20px to their left.
   *
   * Baking the glyph into the value itself cannot align anything, because a 6px
   * status dot, a 14px progress ring and no glyph at all each push the text to a
   * different offset.
   */
  valueGlyph?: React.ReactNode | null;
}

const PropertyRow = React.forwardRef<HTMLDivElement, PropertyRowProps>(
  (
    {
      label,
      icon,
      children,
      onClick,
      editable = false,
      labelWidth = "w-28",
      labelSuffix,
      valueVariant = "text",
      valueGlyph,
      className,
      ...props
    },
    ref,
  ) => {
    const interactive = Boolean(onClick) || editable;

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!onClick) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onClick();
      },
      [onClick],
    );

    return (
      <div
        ref={ref}
        data-slot="property-row"
        data-interactive={interactive ? "true" : "false"}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={onClick ? handleKeyDown : undefined}
        className={cn(
          "group -mx-1 flex min-h-control items-center gap-2 rounded-sm px-1 text-sm",
          "transition-[background-color] duration-100 ease-out-fast",
          "data-[interactive=true]:cursor-pointer data-[interactive=true]:hover:bg-hover",
          "focus-visible:outline-none focus-bar",
          className,
        )}
        {...props}
      >
        <span
          className={cn(
            "flex shrink-0 items-center gap-1.5 text-xs font-medium text-text-tertiary",
            "[&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-text-tertiary",
            labelWidth,
          )}
        >
          {icon}
          <span className="truncate">{label}</span>
          {labelSuffix}
        </span>
        {valueGlyph !== undefined ? (
          <span
            aria-hidden={valueGlyph === null ? "true" : undefined}
            className="flex size-3.5 shrink-0 items-center justify-center [&_svg]:size-3.5"
          >
            {valueGlyph}
          </span>
        ) : null}
        <span
          className={cn(
            "min-w-0 flex-1 text-text",
            valueVariant === "text" && "truncate",
          )}
        >
          {children}
        </span>
      </div>
    );
  },
);
PropertyRow.displayName = "PropertyRow";

export { PropertyRow };
